/**
 * Inventory-related types shared between backend and frontend
 */

/**
 * Lifecycle status of a metadata change set (a batch of staged metadata edits
 * reviewed/exported as a unit).
 */
export enum MetadataChangeSetStatus {
  Draft = "draft",
  Published = "published",
}

/**
 * Change-set-level event history actions: what happened to a metadata change
 * set (not to an individual staged row) — creation, detail edits, status and
 * assignee changes, staged-edit saves, and version restores.
 */
export enum MetadataChangeSetEventAction {
  Created = "created",
  DetailsUpdated = "details_updated",
  StatusChanged = "status_changed",
  AssigneeChanged = "assignee_changed",
  EditsStaged = "edits_staged",
  VersionRestored = "version_restored",
  NewRowAdded = "new_row_added",
  NewRowRemoved = "new_row_removed",
}

/**
 * What produced one version of a change set's staged edits: the change set's
 * creation, someone saving edits to its rows, or someone adding/removing an
 * authored new row.
 */
export enum MetadataChangeSetVersionSource {
  Created = "created",
  EditsSaved = "edits_saved",
  NewRowAdded = "new_row_added",
  NewRowRemoved = "new_row_removed",
}

// ─── Item Master validation rules ────────────────────────────────────────────
// A validation rule declares an invariant over the AS/400 catalog: group the
// rows of one logical ingest schema by some key fields, then require the
// remaining validated fields to agree within each group. Rules are authored,
// activated, and run; each run records the groups that disagreed as violations.

/**
 * What kind of invariant a validation rule checks. Only one kind exists today:
 * fields that must agree across every row of a group.
 */
export enum ValidationRuleType {
  FieldConsistency = "field_consistency",
}

/**
 * Lifecycle status of a validation rule. Only `Active` rules are picked up by
 * scheduled/after-ingest triggers; `Draft` and `Paused` rules can still be run
 * by hand from their detail page.
 */
export enum ValidationRuleStatus {
  Draft = "draft",
  Active = "active",
  Paused = "paused",
}

/** What starts a validation run. */
export enum ValidationRuleTrigger {
  Manual = "manual",
  Scheduled = "scheduled",
  AfterIngest = "after_ingest",
}

/**
 * How a rule proposes to fix a group whose validated fields disagree:
 * take the value most rows carry, take the value one preferred division
 * carries, or propose nothing (report only).
 */
export enum CorrectionStrategy {
  MostCommon = "most_common",
  PreferredSource = "preferred_source",
  None = "none",
}

/** Lifecycle status of one execution of a validation rule. */
export enum ValidationRunStatus {
  Running = "running",
  Succeeded = "succeeded",
  Failed = "failed",
}

/**
 * Whether a violating group still disagrees. A violation is resolved once a
 * later run no longer sees the disagreement (or a correction was applied).
 * An exempted violation still disagrees, but a person has ruled it acceptable:
 * it stays out of the open pile and is never staged into a review, and the
 * exemption is sticky — a recurrence comes back exempted, not open, until
 * someone removes it.
 */
export enum ValidationViolationStatus {
  Open = "open",
  Resolved = "resolved",
  Exempted = "exempted",
}

/**
 * Pricelist-level event history actions: what happened to one uploaded
 * pricelist on its details page — company assignment, extraction re-runs and
 * CSV edits, version restores, deletion, and each of the manually maintained
 * detail fields.
 */
export enum PricelistEventAction {
  Assigned = "assigned",
  Deleted = "deleted",
  ExtractionRetried = "extraction_retried",
  CsvEdited = "csv_edited",
  DivisionPriceChangesUpdated = "division_price_changes_updated",
  TariffChangeFlagChanged = "tariff_change_flag_changed",
  AnticipatedChangeUpdated = "anticipated_change_updated",
  EffectiveDateUpdated = "effective_date_updated",
  ErpUploadDateUpdated = "erp_upload_date_updated",
  ReceivedAsUpdated = "received_as_updated",
  ReceivedDateUpdated = "received_date_updated",
  /**
   * Historical only: the flag was dropped once the log export became a
   * selection. Kept so rows already written under it still resolve a label.
   */
  ExcludeFromLogChanged = "exclude_from_log_changed",
  NotesUpdated = "notes_updated",
  VersionRestored = "version_restored",
}

/**
 * What the vendor actually sent for a price change: an advance notice
 * document, the price file itself, or both. Mirrors the NOTES column of the
 * legacy "PRICE FILE LOG" workbook ("NOTICE" / "FILE" / "NOTICE/FILE").
 */
export enum PricelistReceivedAs {
  Notice = "notice",
  File = "file",
  NoticeAndFile = "notice_file",
}

/**
 * What produced one version of a pricelist's extracted tables: the original
 * upload, a re-run of the extraction, or someone saving edits to the tables.
 */
export enum PricelistVersionSource {
  Upload = "upload",
  Extraction = "extraction",
  Edit = "edit",
}

/**
 * Status of inventory availability check
 */
export enum InventoryStatus {
  /** Available quantity meets or exceeds requested quantity */
  SUFFICIENT = "SUFFICIENT",
  /** Available quantity is less than requested quantity */
  INSUFFICIENT = "INSUFFICIENT",
  /** Unable to determine inventory status (e.g., no connection, item not found) */
  UNKNOWN = "UNKNOWN",
  /** Item is marked as inactive and cannot be fulfilled */
  INACTIVE = "INACTIVE",
}

/**
 * Result of an inventory validation check for a line item
 */
export interface InventoryValidationResult {
  /** Status of the inventory check */
  status: InventoryStatus;
  /** Quantity requested on the line item */
  requestedQuantity: number;
  /** Available quantity (on-hand minus reserved), null if unknown */
  availableQuantity: number | null;
  /** Total on-hand quantity, null if unknown */
  onhandQuantity: number | null;
  /** Reserved quantity, null if unknown */
  reservedQuantity: number | null;
  /** Organization code used for the query */
  organizationCode: string | null;
  /** Item number/SKU used for the query */
  itemNumber: string | null;
  /** Error message if the check failed */
  errorMessage?: string;
  /** Raw item status from the ERP system (e.g. "Active", "Inactive") */
  itemStatus?: string;
}

// ─── Pricelist Update Cases (GEN-2524) ───────────────────────────────────────
// A case is one vendor price change: an `im_pricelist_cases` row holding the
// domain fields, paired 1:1 with a `core_tasks` row holding the work (owner,
// status, comments, timeline). Design contract:
// docs/projectplans/pricelist-update-cases.md.

/**
 * What one file contributes to a case. `notice` is the advance notification,
 * `price_file` the actual prices, `supporting` anything else the change needs
 * to be understood (a cover email, a tariff bulletin, a corrected extract).
 *
 * Not an enum: the value is stored as text and validated by a CHECK constraint
 * on `im_pricelist_case_files.role`, and callers match on the string.
 */
export const PRICELIST_CASE_FILE_ROLES = [
  "notice",
  "price_file",
  "supporting",
] as const;

export type PricelistCaseFileRole = (typeof PRICELIST_CASE_FILE_ROLES)[number];

export function isPricelistCaseFileRole(
  value: string,
): value is PricelistCaseFileRole {
  return (PRICELIST_CASE_FILE_ROLES as readonly string[]).includes(value);
}

/**
 * The task namespace cases are numbered in, giving each case a readable
 * `PC-1042` display id via `buildDisplayId(namespace.key, taskNumber)`.
 *
 * The namespace is created idempotently at first use and its
 * `lastTaskNumber` is never decremented, so case numbers are permanent — the
 * business quotes them in email to vendors.
 */
export const PRICELIST_CASE_NAMESPACE_KEY = "PC";
export const PRICELIST_CASE_NAMESPACE_NAME = "Pricelist Cases";

/**
 * Key of the org's task type for a case's companion task. Anchored to
 * `SystemEntity.IM_PRICELIST_CASE`, which is what makes the existing
 * `core_tasks_open_anchor_unique` partial index read as "at most one OPEN task
 * per case".
 */
export const PRICELIST_CASE_TASK_TYPE_KEY = "pricelist-update-case";
export const PRICELIST_CASE_TASK_TYPE_NAME = "Pricelist update case";

/** Name of the Core Enum holding the case outcome vocabulary. */
export const PRICELIST_CASE_OUTCOME_ENUM_NAME = "Pricelist Case Outcome";

/**
 * Terminal outcomes for a case's task, in display order, seeded as the option
 * names of {@link PRICELIST_CASE_OUTCOME_ENUM_NAME}. A Core Enum rather than a
 * DB enum so the vocabulary stays org-editable.
 *
 * OPEN QUESTION: this list is penciled in from the design document and has not
 * been confirmed with the business (Vera-Lynne Sokoliwski, Rexel). Because it
 * is seeded into a Core Enum, changing it is an org-config edit rather than a
 * migration — but existing rows' `outcome` values are not rewritten.
 */
export const PRICELIST_CASE_OUTCOMES = [
  "loaded",
  "superseded",
  "cancelled",
  "duplicate",
] as const;

export type PricelistCaseOutcome = (typeof PRICELIST_CASE_OUTCOMES)[number];

/**
 * `core_task_actions.action_key` values a pricelist update case writes against
 * its companion task.
 *
 * File attach/detach is a domain fact, not a CRUD diff, so it belongs in the
 * append-only action log rather than in `audit_change_log` (which covers the
 * case's own field changes). Each row carries the document id in its payload,
 * which is what lets the case timeline render the entry with a file chip.
 *
 * Shared because both sides need them: the service writes them and the case
 * timeline renders them by key.
 */
export const PRICELIST_CASE_FILE_ATTACHED_ACTION_KEY =
  "pricelist_case_file_attached";
export const PRICELIST_CASE_FILE_DETACHED_ACTION_KEY =
  "pricelist_case_file_detached";
export const PRICELIST_CASE_FILE_NOTES_CHANGED_ACTION_KEY =
  "pricelist_case_file_notes_changed";
export const PRICELIST_CASE_FILE_DEPRECATED_ACTION_KEY =
  "pricelist_case_file_deprecated";
export const PRICELIST_CASE_FILE_REINSTATED_ACTION_KEY =
  "pricelist_case_file_reinstated";
export const PRICELIST_CASE_COMMENT_ADDED_ACTION_KEY =
  "pricelist_case_comment_added";
export const PRICELIST_CASE_COMMENT_UPDATED_ACTION_KEY =
  "pricelist_case_comment_updated";
export const PRICELIST_CASE_COMMENT_DELETED_ACTION_KEY =
  "pricelist_case_comment_deleted";

/**
 * The case domain columns whose `audit_change_log` diffs the case timeline
 * renders as "old → new". Snake_case, because that is how the trigger keys
 * `changed_fields`.
 *
 * An allowlist rather than "every changed column": `updated_at` changes on
 * every save and `task_id` / `organization_id` are structural, so rendering
 * them would bury the one fact the business asked for (a moved effective
 * date) in noise.
 *
 * `company_id` IS here, unlike the other structural columns, because the
 * vendor is editable — a case opened against the wrong vendor gets moved — and
 * a price change that changed hands with nothing in its timeline would be the
 * one gap the audit trigger exists to close.
 */
export const PRICELIST_CASE_AUDITED_FIELDS = [
  "company_id",
  "company_ids",
  "vendor_numbers",
  "effective_date",
  "received_as",
  "received_date",
  "erp_upload_date",
  "division_price_changes",
  "notes",
  "from_tariff_change",
  "anticipated_vendor_change_percent",
  "vendor_code",
  "national_im_comparing_comments",
  "upload_comments",
  "waiting_on_key",
  "follow_up_at",
  "files_ready_at",
] as const;

export type PricelistCaseAuditedField =
  (typeof PRICELIST_CASE_AUDITED_FIELDS)[number];

export function isPricelistCaseAuditedField(
  value: string,
): value is PricelistCaseAuditedField {
  return (PRICELIST_CASE_AUDITED_FIELDS as readonly string[]).includes(value);
}
